import { router, useForm } from '@inertiajs/react'
import { Gift } from 'lucide-react'
import type { FormEvent } from 'react'

import { BagIcon, ImagePlaceholderIcon } from './Icons'
import { StoreImage } from './StoreImage'
import { useTranslation } from '@/lib/i18n'
import { useCurrency } from '@/lib/currency'
import { useIsMini } from '@/lib/useMediaQuery'
import { trackEvent } from '@/lib/track'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { QuantityStepper } from '@/components/ui/quantity-stepper'
import {
  Sheet,
  SheetCloseButton,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type { CartGift, CartItem, CartState } from '@/types'

interface BagDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cart: CartState
}

/**
 * Spec 08 · Bag drawer, spec 13 · full-screen on mini.
 *
 * 420px right drawer on desktop, full-screen sheet below 560. Reads
 * the cart from globally shared Inertia props (Cartable#inertia_share)
 * and posts mutations through the cart routes with preserveScroll so
 * the page doesn't jump under the drawer.
 */
export default function BagDrawer({ open, onOpenChange, cart }: BagDrawerProps) {
  const { items, count, total, gift } = cart
  const { t } = useTranslation()
  const { format, formatTotal } = useCurrency()
  const isMini = useIsMini()

  const updateQuantity = (productId: number | string, quantity: number) => {
    router.patch(
      '/cart',
      { product_id: productId, quantity },
      { preserveScroll: true, preserveState: true }
    )
  }

  const removeItem = (item: CartItem) => {
    trackEvent('remove_from_cart', {
      label: item.name,
      meta: { product_id: item.productId, quantity: item.quantity },
    })
    // Custom member action: DELETE /cart/remove (delete :remove in routes.rb),
    // NOT DELETE /cart — the latter has no route and returns 404.
    router.delete('/cart/remove', {
      data: { product_id: item.cartKey || item.productId },
      preserveScroll: true,
      preserveState: true,
    })
  }

  const goToCheckout = () => {
    trackEvent('cta_click', {
      label: 'Checkout (bag)',
      meta: { total, items: items.length },
      stage: 'clicked_cta',
    })
    onOpenChange(false)
    // Collect delivery details before the explicit direct-chat handoff.
    router.visit('/checkout')
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={isMini ? 'full' : 'right'} aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle>{t('bag.title', { count })}</SheetTitle>
          <SheetCloseButton label={t('bag.close_aria')} />
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 items-start p-4">
            <EmptyState
              className="w-full"
              icon={<BagIcon size={28} />}
              title={t('bag.empty')}
              description={t('bag.note')}
              action={
                <Button variant="outline" size="compact" onClick={() => onOpenChange(false)}>
                  {t('bag.keep_shopping')}
                </Button>
              }
            />
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-3.5">
              {items.map((item, i) => (
                <div
                  key={item.cartKey || item.productId}
                  className={
                    i > 0 ? 'flex gap-3.5 border-t border-hairline pt-4 mt-4' : 'flex gap-3.5'
                  }
                >
                  <div className="h-[88px] w-[72px] shrink-0 overflow-hidden rounded-md border border-hairline bg-surface">
                    {item.imageUrl ? (
                      <StoreImage
                        src={item.imageUrl}
                        alt={item.name}
                        className="size-full object-cover"
                      />
                    ) : (
                      <span className="flex size-full items-center justify-center text-ink-faint">
                        <ImagePlaceholderIcon size={24} />
                      </span>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col">
                    <div className="flex justify-between gap-2">
                      <span className="text-[15px] font-semibold">{item.name}</span>
                      <span className="text-[15px] font-semibold tabular-nums">
                        {format(item.price, item.prices, item.quantity)}
                      </span>
                    </div>
                    {/* The device a vape case is cut for outranks the
                        resin line — that is what the seller needs. */}
                    {(item.vape || item.material) && (
                      <div className="mt-1 text-[13px] text-ink-faint">
                        {item.vape || item.material} · ×{item.quantity}
                      </div>
                    )}

                    {item.designSummary && <p className="mt-2 text-xs text-ink-muted">{item.designSummary}</p>}
                    <div className="mt-auto flex items-center justify-between gap-3 pt-3">
                      <QuantityStepper
                        value={item.quantity}
                        min={1}
                        max={item.stock}
                        onChange={(quantity) => updateQuantity(item.cartKey || item.productId, quantity)}
                        labels={{
                          decrease: t('bag.decrease_aria'),
                          increase: t('bag.increase_aria'),
                        }}
                      />
                      <Button
                        variant="inline"
                        size="inline"
                        className="text-[13px] text-ink-muted"
                        onClick={() => removeItem(item)}
                      >
                        {t('bag.remove')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}

              {gift?.earned && <GiftLine gift={gift} />}

              {/* Variant 2 · the gift offer. Only while the bag is nonempty
                  and no code has been applied — success replaces it with the
                  gift line alone. */}
              {gift && !gift.earned && <PromoCard />}
            </div>

            <SheetFooter>
              <div className="mb-3.5 flex justify-between text-[17px] font-bold">
                <span>{t('bag.subtotal')}</span>
                <span data-testid="cart-subtotal" className="tabular-nums">
                  {formatTotal(items)}
                </span>
              </div>
              <SheetDescription className="sr-only">{t('bag.note')}</SheetDescription>
              <Button variant="primary" size="block" onClick={goToCheckout}>
                {t('bag.checkout')}
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

/**
 * 04 · Variant 2 — the promo card, shown before a code is applied.
 *
 * Gift icon, what the code earns, then a labelled field and a white
 * Apply. The field is a real controlled input, not a styled span: every
 * edit and paste is upper-cased, mobile keyboards start on capitals, and
 * the server — never the client — decides which code is valid. Pending
 * disables both controls; a rejected code keeps the card and shows the
 * server's localized message inline.
 */
function PromoCard() {
  const { t } = useTranslation()
  const form = useForm<{ code: string }>({ code: '' })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.post('/cart/promotion', { preserveScroll: true, preserveState: true })
  }

  return (
    <div data-testid="gift-promo-card" className="mt-4 rounded-lg border border-hairline bg-surface p-4">
      <div className="flex items-center gap-2 text-[13px] font-semibold">
        <Gift size={16} aria-hidden="true" />
        {t('bag.promo.title')}
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
        {t('bag.promo.description')}
      </p>

      <form onSubmit={submit} noValidate className="mt-3">
        <label htmlFor="cart-promo-code" className="sr-only">
          {t('bag.promo.label')}
        </label>
        <div className="flex gap-2">
          <Input
            id="cart-promo-code"
            name="code"
            value={form.data.code}
            onChange={(event) => {
              form.setData('code', event.target.value.toUpperCase())
              form.clearErrors('code')
            }}
            placeholder={t('bag.promo.placeholder')}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={64}
            aria-invalid={form.errors.code ? true : undefined}
            aria-describedby={form.errors.code ? 'cart-promo-code-error' : undefined}
            disabled={form.processing}
            className="h-11 min-w-0 flex-1 px-3 text-base md:text-sm"
          />
          <Button type="submit" variant="primary" size="compact" className="h-11" processing={form.processing} disabled={form.processing}>
            {t('bag.promo.apply')}
          </Button>
        </div>
        {form.errors.code && (
          <p id="cart-promo-code-error" role="alert" className="mt-2 text-[12px] text-destructive">
            {form.errors.code}
          </p>
        )}
      </form>
    </div>
  )
}

/**
 * 1c · The gift, as a line of its own under the bought ones.
 *
 * It reads like a bag line and is deliberately not one: no stepper, no
 * remove, no price — "Free" in the accent where a figure would be — and
 * it stays out of the count and the subtotal, because it is a gift and
 * not something anyone is paying for. The note names the promo code as
 * what earned it, never the code itself.
 */
function GiftLine({ gift }: { gift: CartGift }) {
  const { t } = useTranslation()

  return (
    <div data-testid="cart-gift" className="mt-4 flex gap-3.5 border-t border-hairline pt-4">
      <div className="h-[88px] w-[72px] shrink-0 overflow-hidden rounded-md border border-signal/25 bg-surface">
        {gift.imageUrl ? (
          <StoreImage src={gift.imageUrl} alt={gift.name} className="size-full object-cover" />
        ) : (
          <span className="flex size-full items-center justify-center text-ink-faint">
            <ImagePlaceholderIcon size={24} />
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2.5">
        <div className="flex flex-col">
          <div className="flex justify-between gap-2">
            <span className="text-[15px] font-semibold">{t('bag.gift.title')}</span>
            <span className="text-[15px] font-semibold text-signal">{t('bag.gift.free')}</span>
          </div>
          <div className="mt-0.5 text-[13px] text-ink-faint">{t('bag.gift.note')}</div>
        </div>

      </div>
    </div>
  )
}
